<template>
  <div>
    <label class="label" name="subscriptionTier">Subscription Type</label>
    <v-select
      v-model="selectedSubscriptionTierId"
      :options="subscriptionTiers"
      :reduce="item => item._id"
      label="name"
      placeholder="Select Subscription Type"
      class="w-full"
      :disabled="subscriptionTiers.length === 0 || isProcessing"
      :clearable="true"
      :closeOnSelect="true"
      @input="handleSubscriptionTierChange"
    />
    <p v-if="subscriptionTiers.length === 0" class="text-xs text-warning mb-3">
      Subscription tier options are empty.
    </p>
    <vs-button
      type="line"
      class="p-0 pb-1 text-xs text-left"
      @click="subscriptionTierPopupHandler"
      v-if="role"
    >
      Manage Subscription Tiers
    </vs-button>
    <vs-popup class="subscription-tier-popup" title="Manage Subscription Tiers" :active.sync="subscriptionTierPopupActive">
      <div class="p-8 flex flex-col gap-5">
        <div>
          <h3>Manage Subscription Tiers</h3>
          <span class="text-base pt-3">Add, edit, or remove subscription tier options</span>
        </div>
        <div class="flex flex-col lg:grid lg:grid-cols-3 gap-4">
          <div class="col-span-1 flex flex-col gap-2">
            <vs-input
              label="Subscription Tier Name"
              class="w-full"
              placeholder="e.g. Health PAYG (Nov 25)"
              v-model="currentSubscriptionTier.name"
              :success="!!(!errors.first('subscriptionTier.nametxtbox') && currentSubscriptionTier.name)"
              val-icon-success="done"
              val-icon-danger="error"
              :danger="errors.first('subscriptionTier.nametxtbox') ? true : false"
              v-validate="subscriptionTierPopupActive ? 'required' : ''"
              name="nametxtbox"
              ref="nametxtbox"
              :danger-text="errors.first('subscriptionTier.nametxtbox')"
              data-vv-as="subscription tier name"
              data-vv-scope="subscriptionTier"
              :disabled="isProcessing"
            />
            <vs-button @click="addEditSubscriptionTierOption" class="mt-2" :disabled="isProcessing">
              {{ isEditSubscriptionTier ? 'Update' : 'Add' }} Option
            </vs-button>
            <vs-button v-if="isEditSubscriptionTier" @click="resetForm()" type="border" :disabled="isProcessing">Cancel</vs-button>
          </div>
          <vs-divider class="lg:hidden"></vs-divider>
          <div class="lg:col-span-2 lg:border lg:border-t-0 lg:border-r-0 lg:border-b-0 lg:border-solid d-theme-border-grey-light lg:pl-4">
            <h5 class="mb-3">Existing Subscription Tier Options</h5>
            <div v-if="subscriptionTiers.length > 0" class="max-h-xs overflow-y-auto">
              <div
                v-for="tier in subscriptionTiers"
                :key="tier._id"
              >
                <div class="flex justify-between items-center">
                  <span>
                    {{ tier.name }}
                  </span>
                  <div class="flex justify-center">
                    <div
                      v-if="currentlyDeletingTierId != tier._id"
                      class="flex gap-2"
                    >
                      <vx-tooltip text="Edit" delay=".5s">
                        <vs-button
                          @click="editSubscriptionTierHandler(tier)"
                          icon-pack="feather"
                          icon="icon-edit"
                          type="flat"
                          :disabled="isProcessing"
                        >
                        </vs-button>
                      </vx-tooltip>
                      <vx-tooltip
                        text="Delete"
                        delay=".5s"
                      >
                        <vs-button
                          @click="deleteSubscriptionTierHandler(tier)"
                          icon-pack="feather"
                          icon="icon-trash-2"
                          type="flat"
                          :disabled="isProcessing"
                        >
                        </vs-button>
                      </vx-tooltip>
                    </div>
                    <div
                      v-if="currentlyDeletingTierId === tier._id"
                      class="flex gap-2"
                      :key="'edit-' + tier._id"
                    >
                      <vx-tooltip
                        text="Confirm Delete?"
                        delay=".5s"
                      >
                        <vs-button
                          @click="confirmDeleteSubscriptionTier(tier)"
                          icon-pack="feather"
                          icon="icon-check"
                          type="flat"
                          color="danger"
                          :disabled="isProcessing"
                        >
                        </vs-button>
                      </vx-tooltip>
                      <vx-tooltip
                        text="Cancel"
                        delay=".5s"
                      >
                        <vs-button
                          @click="currentlyDeletingTierId = ''"
                          icon-pack="feather"
                          icon="icon-x"
                          type="flat"
                          :disabled="isProcessing"
                        >
                        </vs-button>
                      </vx-tooltip>
                    </div>
                  </div>
                </div>
                <vs-divider class="my-1"></vs-divider>
              </div>
            </div>
            <div v-else class="flex items-center justify-center h-32">
              <p>No Subscription Tier options.</p>
            </div>
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import vSelect from 'vue-select';

export default {
  name: 'SubscriptionTierManager',
  components: {
    vSelect,
  },
  props: {
    value: {
      type: [String, Object],
      default: null,
    },
    role: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localSubscriptionTierId: null,
      subscriptionTiers: [],
      subscriptionTierPopupActive: false,
      currentSubscriptionTier: {
        name: '',
        id: '',
      },
      isEditSubscriptionTier: false,
      currentlyDeletingTierId: '',
      isProcessing: false,
      initialized: false,
    };
  },
  computed: {
    selectedSubscriptionTierId: {
      get() {
        return this.localSubscriptionTierId;
      },
      set(next) {
        const id = this.normalizeId(next);
        if (this.localSubscriptionTierId !== id) {
          this.localSubscriptionTierId = id;
        }
      },
    },
  },
  watch: {
    value: {
      handler(newVal) {
        if (!this.isProcessing && this.initialized) {
          const normalized = this.normalizeId(newVal);
          const validId = this.subscriptionTiers.some(tier => tier._id === normalized) ? normalized : null;
          
          if (this.localSubscriptionTierId !== validId) {
            this.localSubscriptionTierId = validId;
          }
        }
      },
      deep: true,
    },
    subscriptionTierPopupActive(newVal) {
      this.currentlyDeletingTierId = '';
      this.isEditSubscriptionTier = false;
      this.isProcessing = false;

      if (!newVal) {
        this.$vs.loading.close();
      }
    },
  },
  methods: {
    ...mapActions('subscriptionTiers', [
      'fetchSubscriptionTiers',
      'createSubscriptionTier',
      'updateSubscriptionTier',
      'deleteSubscriptionTier',
    ]),
    
    normalizeId(input) {
      if (!input) return null;
      if (typeof input === 'object') {
        return input._id || input.id || null;
      }
      return input;
    },

    handleSubscriptionTierChange(value) {
      this.selectedSubscriptionTierId = value;
      this.$emit('input', value);
    },

    async getSubscriptionTiers() {
      try {
        const res = await this.fetchSubscriptionTiers();
        if (res.data && res.data.data) {
          const tiers = res.data.data.docs || res.data.data;
          if (Array.isArray(tiers) && tiers.length) {
            this.subscriptionTiers = tiers.map(({_id, name, createdAt, updatedAt}) => ({
              _id,
              name,
              createdAt,
              updatedAt,
            }));
          } else {
            this.subscriptionTiers = [];
          }
        } else {
          this.subscriptionTiers = [];
        }
      } catch (error) {
        console.error('Error fetching subscription tiers:', error);
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to fetch subscription tiers',
          color: 'danger',
        });
      }
    },

    subscriptionTierPopupHandler() {
      this.subscriptionTierPopupActive = true;
      this.getSubscriptionTiers();
    },

    async addEditSubscriptionTierOption() {
      if (this.currentSubscriptionTier.name && this.currentSubscriptionTier.name.trim() !== '') {
        this.$vs.loading();

        try {
          if (!this.isEditSubscriptionTier) {
            await this.createSubscriptionTier({
              name: this.currentSubscriptionTier.name,
            });
            this.$vs.notify({
              title: 'Success',
              text: 'Subscription tier added successfully.',
              color: 'success',
            });
          } else {
            await this.updateSubscriptionTier({
              id: this.currentSubscriptionTier.id,
              data: {
                name: this.currentSubscriptionTier.name,
              },
            });
            this.$vs.notify({
              title: 'Success',
              text: 'Subscription tier updated successfully.',
              color: 'success',
            });
          }
          
          await this.getSubscriptionTiers();
          this.resetForm();
        } catch (err) {
          this.$vs.notify({
            title: 'Failed',
            text: `Failed to ${this.isEditSubscriptionTier ? 'update' : 'add'} subscription tier. Try again!`,
            color: 'danger',
          });
        } finally {
          this.$vs.loading.close();
        }
      } else {
        this.errors.add({
          field: 'nametxtbox',
          msg: 'Subscription tier name is required',
          scope: 'subscriptionTier',
        });
      }
    },

    async editSubscriptionTierHandler(tier) {
      this.isEditSubscriptionTier = true;
      this.currentSubscriptionTier.name = tier.name;
      this.currentSubscriptionTier.id = tier._id;
      this.currentlyDeletingTierId = '';
      
      this.$nextTick(() => {
        const inputRef = this.$refs.nametxtbox;
        if (inputRef && inputRef.$el) {
          const inputElement = inputRef.$el.querySelector('input');
          if (inputElement) {
            inputElement.focus();
          }
        }
      });
    },

    deleteSubscriptionTierHandler(tier) {
      this.currentlyDeletingTierId = tier._id;
      this.isEditSubscriptionTier = false;
      this.currentSubscriptionTier.name = '';
      this.currentSubscriptionTier.id = '';
      this.$validator.reset();
    },

    async confirmDeleteSubscriptionTier(tier) {
      const deletedId = tier._id;

      if(this.isProcessing) return;
      
      this.isProcessing = true;
      this.$vs.loading();

      try {
        await this.deleteSubscriptionTier(deletedId);

        this.$vs.notify({
          title: 'Success',
          text: 'Subscription tier deleted successfully.',
          color: 'success',
        });

        await this.getSubscriptionTiers();

        if (this.localSubscriptionTierId === deletedId) {
          this.localSubscriptionTierId = null;
          this.$emit('input', null);
        }

        this.resetForm();

      } catch (err) {
        this.$vs.notify({
          title: 'Failed',
          text: 'Failed to delete subscription tier. Try again!',
          color: 'danger',
        });
      } finally {
        this.currentlyDeletingTierId = '';
        this.isProcessing = false;
        this.$vs.loading.close();
      }
    },

    resetForm() {
      this.isEditSubscriptionTier = false;
      this.currentSubscriptionTier.name = '';
      this.currentSubscriptionTier.id = '';
      this.currentlyDeletingTierId = '';
      this.$validator.reset();
    },
  },

  async mounted() {
    this.isProcessing = false;

    await this.getSubscriptionTiers();

    const normalized = this.normalizeId(this.value);
    const validId = this.subscriptionTiers.some(tier => tier._id === normalized) ? normalized : null;
    this.localSubscriptionTierId = validId;

    this.initialized = true;
  },
};
</script>

<style scoped>
.subscription-tier-popup {
  z-index: 9999;
}

.v-select >>> .vs__dropdown-toggle {
  font-size: 14px;
}

.v-select >>> .vs__selected {
  font-size: 14px;
}

.v-select >>> .vs__dropdown-option {
  font-size: 14px;
}

.v-select >>> .vs__dropdown-option--highlight {
  font-size: 14px;
}
</style>

